import { getLoginUserInfo } from "@/api/users";
import ConfirmModal from "@/components/ConfirmModal";
import * as Config from "@/config";
import { axios } from "@/lib/axios";
import { useCsrfToken } from "@/hooks/useCsrfToken";
import ApproverPatternList from "@/containers/admin/ApproverPatterns/ApproverPatternList";
import BasicMapManagement from "@/containers/admin/BasicMap/BasicMapManagement";
import DicDataManagement from "@/containers/admin/Dic/DicDataManagement";
import DocumentManagement from "@/containers/admin/Documents/DocumentManagement";
import { Layers } from "@/containers/admin/Layers/Layers";
import MaterialCollectionListManagement from "@/containers/admin/MaterialCollectionList/MaterialCollectionListManagement";
import DrmPfInformationEdit from "@/containers/admin/Portal/DrmPfInformationEdit";
import DrmPfInformationList from "@/containers/admin/Portal/DrmPfInformationList";
import DrmPfInformationView from "@/containers/admin/Portal/DrmPfInformationView";
import PositionList from "@/containers/admin/Positions/PositionList";
import PostalCodeManagement from "@/containers/admin/PostalCode/PostalCodeManagement";
import TicketDestinationList from "@/containers/admin/TicketDestinations/TicketDestinationList";
import DrmPf from "@/containers/customer/drmPf/DrmPf";
import APIUsagesDetail from "@/containers/customer/drmPf/usages/APIUsagesDetail";
import APIUsageHistory from "@/containers/customer/usageHistory/UsageHistory";
import Data from "@/containers/data/Data";
import { NaviAnswer } from "@/containers/data/Navi/NaviAnswer";
import TicketSearch from "@/containers/data/Ticket/TicketSearch";
import Report from "@/containers/report/Report";
import { useAxiosRouteStore } from "@/stores/axiosRoute";
import { useLoadingStore } from "@/stores/loading";
import { useMessageModalStore } from "@/stores/message";
import { useProjectStore } from "@/stores/project";
import { useUserStore } from "@/stores/user";
import { useEffect } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
  useNavigate,
} from "react-router-dom";
import SideMenu from './components/SideMenu';
import Header from "./components/Header";
import Admin from "./containers/admin/Admin";
import DataManagement from "./containers/admin/DataManagement/DataManagement";
import Dashboard from "./containers/dashboard/Dashboard";
import GroupEdit from "./containers/admin/Groups/GroupEdit";
import GroupList from "./containers/admin/Groups/GroupList";
import InformationEdit from "./containers/admin/Information/InformationEdit";
import InformationList from "./containers/admin/Information/InformationList";
import UserEdit from "./containers/admin/Users/UserEdit";
import UserList from "./containers/admin/Users/UserList";

import CustomerTicket, {
  CustomerTicketPageType,
} from "@/containers/customer/Ticket/Ticket";
import Login from "./containers/auth/Login";
import PasswordChange from "./containers/auth/PasswordChange";
import Top from "./containers/auth/Top";
import BasicInfoDetail from "./containers/customer/basicinfo/BasicInfoDetail";
import BasicInfoEdit from "./containers/customer/basicinfo/BasicInfoEdit";
import BasicInfoNew from "./containers/customer/basicinfo/BasicInfoNew";
import CustomersList from "./containers/customer/CustomersList";
import DrmPfDetail from "./containers/customer/drmPf/basic/DrmPfDetail";
import DrmPfEdit from "./containers/customer/drmPf/basic/DrmPfEdit";
import DrmPfDownload from "./containers/customer/drmPf/download/DocumentDownload";
import ProgressList from "./containers/progress/ProgressList";

function App() {
  const user = useUserStore((state) => state.user);
  const [selectedProject, selectedProjectCategory] = useProjectStore(
    (state) => [state.project, state.projectCategory]
  );

  // CSRFトークンの初期化
  const { csrfToken, loading: csrfLoading, error: csrfError } = useCsrfToken();

  useEffect(() => {
    document.title = Config.TITLE;
  }, []);

  // CSRFトークン取得中はローディング表示
  if (csrfLoading) {
    return (
      <div className="uk-position-fixed uk-position-cover uk-flex uk-flex-center uk-flex-middle">
        <div className="uk-position-center">
          <span uk-spinner="ratio: 5"></span>
        </div>
      </div>
    );
  }

  // CSRFトークン取得失敗時もローディング扱い
  if (csrfError || !csrfToken) {
    return (
      <div className="uk-position-fixed uk-position-cover uk-flex uk-flex-center uk-flex-middle">
        <div className="uk-position-center">
          <span uk-spinner="ratio: 5"></span>
        </div>
      </div>
    );
  }

  // TODO: react-router v6対応
  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route element={<Layout />}>
          <Route path="/" element={<TopRoute />} />
          <Route path="/top" element={<TopRoute />} />
          <Route path="/login" element={<Login />} />
          <Route element={<ProtectedUserRoute />}>
            <Route path="/password_change" element={<PasswordChange />} />
          </Route>
          <Route element={<ProtectedProjectRoute />}>
            <Route path="/dashboard/:projectId" element={<Dashboard />} />
            <Route path="/progress" element={<ProgressList />} />
            <Route path="/customer" element={<CustomersList />} />
            <Route path="/customer/basicinfo/new" element={<BasicInfoNew />} />
            <Route
              path="/customer/basicinfo/detail/:customerId"
              element={<BasicInfoDetail />}
            />
            <Route
              path="/customer/basicinfo/edit/:customerId"
              element={<BasicInfoEdit />}
            />
            <Route path="/customer/drmpf/api/:customerId" element={<APIUsagesDetail />} />

            <Route path="/customer/drmpf" element={<DrmPf />}>
              <Route path="detail/:customerId" element={<DrmPfDetail />} />
              <Route path="edit/:customerId" element={<DrmPfEdit />} />
              <Route path="download/:customerId" element={<DrmPfDownload />} />
            </Route>
            <Route path="/customer/apiUsage" element={<APIUsageHistory />} />
            <Route
              path="/customer/ticket/:customerId"
              element={<CustomerTicket isType={CustomerTicketPageType.List} />}
            />
            <Route
              path="/customer/ticket/:customerId/search"
              element={
                <CustomerTicket isType={CustomerTicketPageType.Search} />
              }
            />
            <Route
              path="/customer/ticket/:customerId/new"
              element={<CustomerTicket isType={CustomerTicketPageType.New} />}
            />
            <Route
              path="/customer/ticket/:customerId/:ticketId"
              element={
                <CustomerTicket isType={CustomerTicketPageType.Detail} />
              }
            />
            <Route path="/report" element={<Report />} />
            {/* 長くなってきたらDataにRoute移動 */}
            <Route
              path="/progress/:projectCategoryId/new/attributes"
              element={<Data isType={Config.Data.AttributeNew} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/attributes"
              element={<Data isType={Config.Data.AttributeDetail} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/attributes/:tabId"
              element={<Data isType={Config.Data.AttributeDetail} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/attributes/edit"
              element={<Data isType={Config.Data.AttributeEdit} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/attributes/:tabId/edit"
              element={<Data isType={Config.Data.AttributeEdit} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/attributes/copy"
              element={<Data isType={Config.Data.AttributeCopy} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/files/new"
              element={<Data isType={Config.Data.FileNew} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/files/edit/:fileId"
              element={<Data isType={Config.Data.FileEdit} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/files"
              element={<Data isType={Config.Data.FileList} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/apply_areas"
              element={<Data isType={Config.Data.ApplyAreaList} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/apply_areas/edit"
              element={<Data isType={Config.Data.ApplyAreaListEdit} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/ticket"
              element={<Data isType={Config.Data.Ticket} />}
            />
            <Route
              path="/progress/:projectCategoryId/ticket/search"
              element={<TicketSearch />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/public"
              element={<Data isType={Config.Data.PublicDetail} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/public/:faqId"
              element={<Data isType={Config.Data.PublicDetail} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/public/new"
              element={<Data isType={Config.Data.PublicEdit} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/public/:faqId/edit"
              element={<Data isType={Config.Data.PublicEdit} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/approve"
              element={<Data isType={Config.Data.ApproveDetail} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/approve/edit"
              element={<Data isType={Config.Data.ApproveEdit} />}
            />
            <Route
              path="/progress/:projectCategoryId/approve/new"
              element={<Data isType={Config.Data.ApproveEdit} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/approve/copy"
              element={<Data isType={Config.Data.ApproveCopy} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/forms"
              element={<Data isType={Config.Data.Form} />}
            />
            <Route
              path="/progress/:projectCategoryId/:dataId/forms/:applyPdf"
              element={<Data isType={Config.Data.Form} />}
            />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/groups" element={<GroupList />} />
            <Route path="/admin/groups/:id/edit" element={<GroupEdit />} />
            <Route path="/admin/groups/new" element={<GroupEdit />} />
            <Route path="/admin/users" element={<UserList />} />
            <Route path="/admin/users/:id/edit" element={<UserEdit />} />
            <Route path="/admin/users/:id/duplicate" element={<UserEdit />} />
            <Route path="/admin/users/new" element={<UserEdit />} />
            <Route path="/admin/data" element={<DataManagement />} />
            <Route
              path="/admin/postal-code"
              element={<PostalCodeManagement />}
            />
            <Route
              path="/admin/ticket-destination"
              element={<TicketDestinationList />}
            />
            <Route path="/admin/information" element={<InformationList />} />
            <Route
              path="/admin/information/new"
              element={<InformationEdit />}
            />
            <Route
              path="/admin/information/:id/edit"
              element={<InformationEdit />}
            />
            <Route
              path="/admin/information/:id/duplicate"
              element={<InformationEdit />}
            />
            <Route
              path="/admin/material-collection-list"
              element={<MaterialCollectionListManagement />}
            />
            <Route path="/admin/dic" element={<DicDataManagement />} />
            <Route path="/admin/basic-map" element={<BasicMapManagement />} />
            <Route
              path="/admin/document/new"
              element={
                <DocumentManagement
                  isType={Config.DocumentManagement.FileNew}
                />
              }
            />
            <Route
              path="/admin/document/edit/:fileId"
              element={
                <DocumentManagement
                  isType={Config.DocumentManagement.FileEdit}
                />
              }
            />
            <Route
              path="/admin/document"
              element={
                <DocumentManagement
                  isType={Config.DocumentManagement.FileList}
                />
              }
            />
            <Route path="/admin/portal" element={<DrmPfInformationList />} />
            <Route
              path="/admin/portal/new"
              element={<DrmPfInformationEdit />}
            />
            <Route
              path="/admin/portal/:id"
              element={<DrmPfInformationView />}
            />
            <Route
              path="/admin/portal/:id/edit"
              element={<DrmPfInformationEdit />}
            />
            <Route
              path="/admin/portal/:id/duplicate"
              element={<DrmPfInformationEdit />}
            />
            <Route path="/admin/layer" element={<Layers />} />
            <Route path="/admin/positions" element={<PositionList />} />
            <Route
              path="/admin/approverPatterns"
              element={<ApproverPatternList />}
            />
          </Route>
        </Route>
        <Route path="/navi/answer/:param" element={<NaviAnswer />} />
      </>
    )
  );

  return <RouterProvider router={router} />;
}

const Layout = () => {
  const user = useUserStore(state => state.user);
  const location = useLocation();

  return (
    <>
      <Loading />
      <Auth />
      <MessageModal />
      <AxiosNavigationHandler />
      {user && <LastVisitedSync />}
      <div id="modal-root" />
      <div className="main-container">
        {(user && user.is_changed_password === 1 && location.pathname !== "/login") && <SideMenu />}
        <main className="content">
          <Header />
          <Outlet />
        </main>
      </div>
    </>
  );
};

function Auth() {
  const location = useLocation();
  const navigate = useNavigate();
  const setUser = useUserStore((state) => state.setUser);
  const setLoading = useLoadingStore((state) => state.setLoading);
  const setMessage = useMessageModalStore((state) => state.setMessage);

  function getUserData() {
    setLoading(true);
    getLoginUserInfo()
      .then((result) => {
        setUser(result.data);
        // if (location.pathname === "/login" || location.pathname === "/") {
        //   navigate("/progress");
        // }
      })
      .catch((error) => {
        if (!error.response) {
          setMessage(Config.MESSAGE_NO_E39);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    getUserData();
  }, []);

  // 検討: 子コンポーネントに配置して表示切り替え
  return null;
}

function Loading() {
  const isLoading = useLoadingStore((state) => state.isLoading);
  return isLoading ? (
    <div
      className="uk-position-fixed uk-position-cover uk-flex uk-flex-center uk-flex-middle"
      style={{ backgroundColor: "rgba(255, 255, 255, 0.5)", zIndex: 1000 }}
    >
      <div className="uk-position-center">
        <span uk-spinner="ratio: 5"></span>
      </div>
    </div>
  ) : null;
}

// axios.tsからuseNavigateが使えないため
function AxiosNavigationHandler() {
  const [axiosRoute, setAxiosRoute] = useAxiosRouteStore((state) => [
    state.axiosRoute,
    state.setAxiosRoute,
  ]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (
      axiosRoute !== "" &&
      location.pathname !== "/login" &&
      location.pathname !== "/"
    ) {
      navigate("/login", { state: { from: location }, replace: true });
      setAxiosRoute("");
    }
    return () => {};
  }, [axiosRoute]);

  return null;
}

function LastVisitedSync() {
  const user = useUserStore(state => state.user);
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    if (!user || user.is_changed_password !== 1) return;
    if (pathname === '/login') return;

    const path = `${pathname}${search || ''}${hash || ''}`;

    const ctrl = new AbortController();
    const t = setTimeout(() => {
      axios
        .post('/api/v1/users/setLastVisitedPath', { path }, { signal: ctrl.signal })
        .catch(() => {});
    }, 300);

    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [user, pathname, search, hash]);

  return null;
}

function MessageModal() {
  const [message, setMessage] = useMessageModalStore((state) => [
    state.message,
    state.setMessage,
  ]);
  return (
    <ConfirmModal
      text={message}
      confirmButtonText="OK"
      isShow={message !== ""}
      onConfirm={() => {
        setMessage("");
      }}
    />
  );
}

const TopRoute = () => {
  const user = useUserStore((state) => state.user);
  const location = useLocation();
  const [
    selectedProjectCategory,
    selectedAdminCategory,
  ] = useProjectStore(state => [
    state.projectCategory,
    state.adminCategory,
  ]);
  return user ? <Top /> : <Login />;
};

const ProtectedUserRoute = () => {
  const user = useUserStore((state) => state.user);
  const location = useLocation();
  const [
    selectedProjectCategory, 
    selectedAdminCategory, 
  ] = useProjectStore(state => [
    state.projectCategory,
    state.adminCategory,
  ]);
  return user ? <Outlet /> : <Login />;
};

const ProtectedProjectRoute = () => {
  const user = useUserStore((state) => state.user);
  const location = useLocation();
  const [
    selectedProjectCategory,
    selectedAdminCategory,
  ] = useProjectStore(state => [
    state.projectCategory,
    state.adminCategory,
  ]);
  return user && (selectedProjectCategory || selectedAdminCategory) ? <Outlet /> : null;
};

export default App;
